import {
  type Stock,
  getFirstAttributeValue,
  type Variant,
} from '@scayle/storefront-nuxt'
import type {
  VariantPrice,
  VariantDetail,
  Product,
} from '@scayle/storefront-api'
import { useLensSelectionState } from './useLensSelectionState'
import { useFimVariant } from '~/composables/useFimVariant'

export type Quantity = {
  left: number
  right: number
}

export type DeliveryForecastAttribute = {
  key: string
  label: string
  value: string
}

const getFirstVariant = (variants: Ref<VariantDetail[] | null>) =>
  variants.value?.[0]
const isVariantSelected = (
  variant: VariantDetail | undefined,
  selectedVariantId: string | undefined,
  quantity: number,
) => Boolean(variant && variant.id && selectedVariantId?.length && quantity > 0)
const isVariantInStock = (variant: VariantDetail | undefined) =>
  variant &&
  (variant.stock?.quantity > 0 ||
    variant.stock?.isSellableWithoutStock ||
    false)

export function useLenses(product: Ref<Product | null>) {
  const {
    data: variantsLeft,
    fetch: fetchVariantLeft,
    fetching: fetchingLeft,
  } = useFimVariant(`variant-${product.value?.id}-left`)

  const {
    data: variantsRight,
    fetch: fetchVariantRight,
    fetching: fetchingRight,
  } = useFimVariant(`variant-${product.value?.id}-right`)

  const { selectedVariantsIds, quantity, checkedEyes } = useLensSelectionState()

  const variantLeft = computed(() => getFirstVariant(variantsLeft))
  const variantRight = computed(() => getFirstVariant(variantsRight))

  const hasSelectedVariant = computed(() => ({
    left: isVariantSelected(
      variantLeft.value,
      selectedVariantsIds.value.left,
      quantity.value.left,
    ),
    right: isVariantSelected(
      variantRight.value,
      selectedVariantsIds.value.right,
      quantity.value.right,
    ),
  }))

  const inStockLeft = computed(() => isVariantInStock(variantLeft.value))
  const inStockRight = computed(() => isVariantInStock(variantRight.value))

  const availableAndInStock = computed(() => {
    return {
      left: hasSelectedVariant.value.left && inStockLeft.value,
      right: hasSelectedVariant.value.right && inStockRight.value,
    }
  })

  const packageUnits = computed(() => {
    const allSiblings = [
      product.value,
      ...(product?.value?.siblings || []),
    ].filter(Boolean)

    const units = allSiblings
      .map((s) => {
        const numberOfLenses = parseInt(
          getFirstAttributeValue(s.attributes, 'numberOfLenses')?.label || '',
          10,
        )
        return numberOfLenses
          ? {
              numberOfLenses,
              pricePerUnit: s.priceRange?.max.reference?.withTax,
              price: s.priceRange?.max.withTax,
              bapiPrice: { ...s.priceRange?.max } as VariantPrice,
              productId: s.id,
              lowestPrice: getLowestPriorPrice(
                s.priceRange?.max,
                s.lowestPriorPrice,
              ),
              isSoldOut: s.isSoldOut,
              referenceKey: s.referenceKey,
              discount: 0,
            }
          : undefined
      })
      .filter(Boolean)
      .sort((a, b) => a.numberOfLenses - b.numberOfLenses)

    const mostExpensivePackage = [...units].sort(
      (a, b) => (b?.pricePerUnit ?? 0) - (a?.pricePerUnit ?? 0),
    )[0]

    units.forEach((unit) => {
      if (
        mostExpensivePackage.numberOfLenses !== unit.numberOfLenses &&
        unit.pricePerUnit &&
        mostExpensivePackage.pricePerUnit
      ) {
        unit.discount = Math.floor(
          Math.abs(
            (unit.pricePerUnit / mostExpensivePackage.pricePerUnit - 1) * 100,
          ),
        )
      }
    })

    return units
  })

  const isValidFetch = (
    currentVariant: VariantDetail | undefined,
    newVariantId: string | undefined,
  ): newVariantId is string =>
    Boolean(newVariantId && currentVariant?.id.toString() !== newVariantId)

  const fetchVariants = (variantIds: EyeVariantIds) => {
    const fetchers = []
    if (isValidFetch(variantLeft.value, variantIds.left)) {
      fetchers.push(
        fetchVariantLeft({
          ids: [+variantIds.left],
          include: { attributes: 'all' },
        }),
      )
    }

    if (isValidFetch(variantRight.value, variantIds.right)) {
      fetchers.push(
        fetchVariantRight({
          ids: [+variantIds.right],
          include: { attributes: 'all' },
        }),
      )
    }
    return Promise.all(fetchers)
  }

  const packagePrice = computed(() => {
    return (
      packageUnits?.value?.find(
        (packageUnit) => packageUnit.productId === product?.value?.id,
      )?.bapiPrice ?? product?.value?.priceRange?.max
    )
  })

  const numberOfLenses = computed(
    () =>
      getFirstAttributeValue(product?.value?.attributes, 'numberOfLenses')
        ?.label,
  )

  // sort stocks by availability & quantity
  // worst one first
  const chooseStock = (...stocks: Array<Variant['stock'] | undefined>) => {
    const [result] = stocks.filter(Boolean).sort((a, b) => {
      if (!a.quantity && !b.quantity) {
        return a.isSellableWithoutStock != b.isSellableWithoutStock
          ? a.isSellableWithoutStock
            ? 1
            : -1
          : 0
      }

      return a.quantity - b.quantity
    })

    return result
  }

  const combinedVariantStock = computed<Stock | undefined>(() => {
    if (
      (checkedEyes.value.includes('left') && !hasSelectedVariant.value.left) ||
      (checkedEyes.value.includes('right') && !hasSelectedVariant.value.right)
    ) {
      // at least one variant could not be found
      return undefined
    }

    if (hasSelectedVariant.value.left && hasSelectedVariant.value.right) {
      return chooseStock(variantLeft.value?.stock, variantRight.value?.stock)
    } else if (hasSelectedVariant.value.left) {
      return variantLeft.value?.stock
    } else if (hasSelectedVariant.value.right) {
      return variantRight.value?.stock
    }
  })

  return {
    availableAndInStock,
    combinedVariantStock,
    fetchVariants,
    fetching: computed(() => fetchingLeft.value || fetchingRight.value),
    hasSelectedVariant,
    numberOfLenses,
    packagePrice,
    packageUnits,
    variantLeft,
    variantRight,
  }
}
